import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { authErrorKey } from "@/lib/auth-error";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [
      { title: "Şifremi Unuttum — JUËN" },
      { name: "description", content: "JUËN şifrenizi sıfırlamak için e-posta adresinizi girin." },
    ],
  }),
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (error) {
      toast.error(t(authErrorKey(error.message)));
      return;
    }
    setSent(true);
    toast.success(t("forgot.sentToast"));
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-md px-4 py-16">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold tracking-tight">{t("forgot.title")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("forgot.subtitle")}
          </p>
        </div>

        {sent ? (
          <div className="space-y-5 rounded-2xl border border-border bg-card p-6 text-center shadow-card md:p-8">
            <p className="text-sm text-foreground">
              <strong>{email}</strong> {t("forgot.sentBody")}
            </p>
            <Link to="/login" className="inline-block text-sm font-medium text-primary hover:underline">
              {t("forgot.backToLogin")}
            </Link>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-5 rounded-2xl border border-border bg-card p-6 shadow-card md:p-8"
          >
            <div className="space-y-2">
              <Label htmlFor="email">{t("forgot.email")}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <Button type="submit" disabled={loading} className="h-12 w-full text-base">
              {loading ? t("forgot.sending") : t("forgot.submit")}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              <Link to="/login" className="font-medium text-primary hover:underline">
                {t("forgot.backToLogin")}
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
